import React from 'react';
import { Card } from '../Card';
import { Settings } from '../../services/settings.service';

interface SystemSettingsProps {
  settings: Settings | null;
  dbHealth: any;
  checkingHealth: boolean;
  onCheckDatabaseHealth: () => void;
}

export const SystemSettings: React.FC<SystemSettingsProps> = ({
  settings,
  dbHealth,
  checkingHealth,
  onCheckDatabaseHealth,
}) => {
  return (
    <div className="space-y-6">
      {/* Database Health Check */}
      <Card>
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <span className="mr-2">Database Health</span>
        </h2>

        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Check database connection and verify all required tables exist
          </p>

          {/* Check Database Button */}
          <div>
            <button
              onClick={onCheckDatabaseHealth}
              disabled={checkingHealth}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded font-medium transition"
            >
              {checkingHealth ? 'Checking...' : 'Check Database'}
            </button>
          </div>

          {/* Database Health Status */}
          {dbHealth && (
            <div className="bg-gray-900 rounded p-4 space-y-3">
              {/* Overall Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-300">Status:</span>
                <span
                  className={`text-sm font-semibold ${
                    dbHealth.status === 'healthy'
                      ? 'text-green-500'
                      : dbHealth.status === 'warning'
                      ? 'text-yellow-500'
                      : 'text-red-500'
                  }`}
                >
                  {dbHealth.status === 'healthy' && 'Healthy'}
                  {dbHealth.status === 'warning' && 'Warning'}
                  {dbHealth.status === 'error' && 'Error'}
                </span>
              </div>

              {/* Connection Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-300">Connection:</span>
                <span
                  className={`text-sm ${
                    dbHealth.connection === 'connected' ? 'text-green-500' : 'text-red-500'
                  }`}
                >
                  {dbHealth.connection === 'connected' ? 'Connected' : 'Disconnected'}
                </span>
              </div>

              {/* Tables Status */}
              {Object.keys(dbHealth.tables).length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-300 mb-2">Tables:</div>
                  <div className="space-y-2">
                    {Object.entries(dbHealth.tables).map(([tableName, tableInfo]: [string, any]) => (
                      <div key={tableName} className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">{tableName}</span>
                        <div className="flex items-center gap-2">
                          {tableInfo.exists && (
                            <span className="text-xs text-gray-500">
                              ({tableInfo.recordCount} records)
                            </span>
                          )}
                          <span
                            className={`${
                              tableInfo.status === 'ok' ? 'text-green-500' : 'text-red-500'
                            }`}
                          >
                            {tableInfo.status === 'ok' ? 'OK' : 'Error'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Settings Count */}
              {dbHealth.settingsCount !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-300">Settings Loaded:</span>
                  <span className="text-sm text-gray-400">{dbHealth.settingsCount}</span>
                </div>
              )}

              {/* Issues */}
              {dbHealth.issues && dbHealth.issues.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-red-400 mb-2">Issues:</div>
                  <ul className="list-disc list-inside space-y-1">
                    {dbHealth.issues.map((issue: string, index: number) => (
                      <li key={index} className="text-xs text-red-300">
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* System Information */}
      <Card>
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <span className="mr-2">System Information</span>
        </h2>

        <div className="space-y-3 text-sm">
          {settings?.google_sheets_service_account_email && (
            <div className="bg-gray-900 rounded p-3">
              <div className="text-gray-400 mb-1">Service Account:</div>
              <div className="font-mono text-blue-400">
                {settings.google_sheets_service_account_email.value}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Make sure your Google Sheet is shared with this email address
              </div>
            </div>
          )}

          {settings?.current_delivered_ebitda && (
            <div className="bg-gray-900 rounded p-3">
              <div className="text-gray-400 mb-1">Current EBITDA:</div>
              <div className="text-lg font-semibold text-green-500">
                £{(parseInt(settings.current_delivered_ebitda.value) / 1000000).toFixed(1)}M
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
